import { useEffect, useMemo } from 'react'

import {
  useGetClientFields,
  useGetYardiPropertyIds,
} from '@/api/communication/get-recipient-location-filters'

import { getPropertyFilterOptions } from './helpers'

export type FilterOption = { id: string | number; text: string }

type FormChangeEvent = { target: { name: string; value: unknown } }

type SelectedProperty = {
  id: number
  yardiManagementId?: string | null
  yardiPropertyId?: string | null
}

type PreviewData = {
  yardiPropertyId?: string | null
  buildingNumber?: string | null
}

type Params = {
  selectedProperties: SelectedProperty[]
  previewData?: PreviewData | null
  handleChange: (event: FormChangeEvent) => void
  setFieldValue: (field: string, value: unknown) => void
}

export const useRecipientLocationFilters = ({
  selectedProperties,
  previewData,
  handleChange,
  setFieldValue,
}: Params) => {
  const singleProperty = selectedProperties?.length === 1 ? selectedProperties[0] : undefined
  const isYardiProperty = !!(singleProperty?.yardiManagementId && singleProperty?.yardiPropertyId)

  const { data: filters } = useGetClientFields(singleProperty?.id)
  const { data: yardiFilters = [] } = useGetYardiPropertyIds(singleProperty?.id, isYardiProperty)

  const yardiPropertyOptions: FilterOption[] = useMemo(
    () =>
      yardiFilters.length
        ? getPropertyFilterOptions(yardiFilters.map(({ yardiPropertyId }) => yardiPropertyId))
        : [],
    [yardiFilters],
  )

  const buildingOptions: FilterOption[] = useMemo(
    () => (filters ? getPropertyFilterOptions(filters.buildingNumber) : []),
    [filters],
  )

  const floorOptions: FilterOption[] = useMemo(
    () => (filters ? getPropertyFilterOptions(filters.floor) : []),
    [filters],
  )

  useEffect(() => {
    if (previewData?.yardiPropertyId) {
      const selectedYardiProperty = yardiPropertyOptions.find(
        (option) => option.id === previewData.yardiPropertyId,
      )
      handleChange({ target: { name: 'yardiPropertyId', value: selectedYardiProperty } })
    }

    if (previewData?.buildingNumber) {
      const selectedBuildingNumber = buildingOptions.find(
        (option) => option.id === previewData.buildingNumber,
      )
      handleChange({ target: { name: 'buildingNumber', value: selectedBuildingNumber } })
    }
  }, [previewData, yardiPropertyOptions, buildingOptions])

  const resetLocationFilters = () => {
    setFieldValue('yardiPropertyId', null)
    setFieldValue('buildingNumber', null)
    setFieldValue('floor', [])
  }

  return {
    yardiPropertyOptions,
    buildingOptions,
    floorOptions,
    isYardiProperty,
    resetLocationFilters,
  }
}
